export const layer1Template = `# Layer 1：Marveland CMS 骨架生成

> 目標：生成一個**可編譯通過**的專案骨架，包含完整型別定義、API 路由骨架、前台路由骨架。
> **禁止**：生成任何 UI 細節、業務邏輯、動畫效果、樣式設計。
> 輸出要求：執行 \`npm install && npm run build\` 必須成功。

---

## 前置條件

- 無。這是專案的第一個 Layer。

---

## 1. 品牌與內容資料（直接用於 default.ts）

\`\`\`
中文公司名稱：金晉物業代理有限公司
英文公司名稱：Marveland Property Agency Limited
牌照號碼：C-076840
成立年份：2018
地址：新界葵涌葵昌路58-70號永祥工業大廈2樓 A7-A8室
公司電話：(852) 3619 9530
WhatsApp：(852) 9236 8966、(852) 9545 5427
Facebook：https://www.facebook.com/marveland
Instagram：https://www.instagram.com/marvelandproperty
服務時間：週一至週五 09:00 - 18:00（星期六及公眾假期休息）

公司簡介：
「本公司從事多年工商廈、地舖租售買賣。金晉物業代理是一家擁有多年經驗的專業房地產公司，專注於工商廈、地舖的租售買賣。無論您是尋找合適的辦公室、工作間，還是工業用的貨倉或半倉寫等，我們都能提供全面的服務。我們的團隊由資深的房地產專家組成，致力於為客戶提供度身定制的解決方案，確保每一筆交易都順利進行。金晉物業代理以誠信、專業和高效為宗旨，致力於滿足客戶的需求，幫助他們在香港的房地產市場中實現最大價值。如果您有任何工商廈租售買賣的需求，金晉物業代理將是您值得信賴的合作夥伴。」

業務範圍：市場分析、投資建議、銀行估價、市場成交及按揭轉介、一條龍服務、應有盡有。

銷售團隊：
- Peggy Leung / 高級物業顧問 / 85292368966
- Billy Sin / 工商舖物業顧問 / 85295455427
- Faye Yeung / 投資物業顧問 / 85251118475
- Joanne Lee / 租務顧問 / 85291876688
- Mandy Chiu / 客戶關係顧問 / 85293879672
- Suki Lam / 市場分析顧問 / 85267748799
- May Leung / 物業顧問 / 85261828966
\`\`\`

---

## 2. 技術棧（嚴格遵守）

- 前端：React 18 + TypeScript + Vite + Tailwind CSS
- 後端：Cloudflare Worker + Hono + TypeScript
- 儲存：Cloudflare KV namespace \`CMS_DATA\`
- 媒體：KV base64（預設），可選 R2 \`MEDIA_BUCKET\`
- 部署：Wrangler 3
- 套件管理：npm

---

## 3. 專案結構（必須精確建立）

\`\`\`
marveland/
├── apps/
│   ├── web/
│   │   ├── public/_redirects
│   │   ├── src/
│   │   │   ├── main.tsx
│   │   │   ├── App.tsx
│   │   │   ├── index.css
│   │   │   ├── vite-env.d.ts
│   │   │   ├── types/cms.ts
│   │   │   ├── context/CMSContext.tsx
│   │   │   ├── pages/Home.tsx
│   │   │   ├── pages/About.tsx
│   │   │   ├── pages/Properties.tsx
│   │   │   ├── pages/PropertyDetail.tsx
│   │   │   ├── pages/OwnerListing.tsx
│   │   │   ├── pages/ContactPage.tsx
│   │   │   ├── pages/Admin.tsx
│   │   │   ├── pages/NotFound.tsx
│   │   │   ├── components/layout/Header.tsx
│   │   │   ├── components/layout/Footer.tsx
│   │   │   ├── utils/helpers.ts
│   │   │   └── utils/propertyFilters.ts
│   │   ├── package.json
│   │   ├── tsconfig.json
│   │   ├── tsconfig.app.json
│   │   ├── tsconfig.node.json
│   │   ├── tailwind.config.js
│   │   ├── postcss.config.js
│   │   └── vite.config.ts
│   └── cms-worker/
│       ├── src/index.ts
│       ├── src/types.ts
│       ├── src/data/default.ts
│       ├── wrangler.toml
│       └── package.json
├── package.json
└── README.md
\`\`\`

---

## 4. 型別定義

在 \`apps/web/src/types/cms.ts\` 與 \`apps/cms-worker/src/types.ts\` 中定義**完全一致的內容**：

\`\`\`ts
export type ListingType = 'sale' | 'rent' | 'sale_or_rent';
export type PropertyCategory = 'office' | 'industrial' | 'shop' | 'car_park';
export type District = 'tsuen_wan' | 'kwai_chung' | 'lai_chi_kok' | 'kowloon' | 'all';

export interface Property {
  id: string;
  code: string;
  title: string;
  titleTraditional?: string;
  titleEnglish?: string;
  listingType: ListingType;
  category: PropertyCategory;
  district: District;
  location: string;
  address: string;
  addressTraditional?: string;
  addressEnglish?: string;
  mapLink?: string;
  price: number;
  priceUnit: string;
  rentPrice?: number;
  rentPriceUnit?: string;
  areaGross: number;
  areaSaleable?: number;
  description: string;
  descriptionTraditional?: string;
  descriptionEnglish?: string;
  images: string[];
  floorPlan?: string;
  tags: string[];
  features: string[];
  featured: boolean;
  agentId: string;
  publishDate?: string;
}

export interface Agent {
  id: string;
  name: string;
  title: string;
  whatsapp: string;
  email?: string;
  photo?: string;
}

export interface NewsItem {
  id: string;
  title: string;
  date: string;
  summary: string;
  content?: string;
  image: string;
  category: string;
}

export interface Inquiry {
  id: string;
  type: 'owner_listing' | 'contact' | 'property_inquiry';
  name: string;
  phone: string;
  whatsapp?: string;
  email?: string;
  address?: string;
  propertyType?: string;
  listingType?: string;
  area?: string;
  expectedPrice?: string;
  message?: string;
  propertyId?: string;
  createdAt: string;
  status: 'new' | 'contacted' | 'closed' | 'approved' | 'rejected';
  rejectReason?: string;
}

export interface MediaFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  data?: string;
  url?: string;
  createdAt: string;
}

export interface AnalyticsEvent {
  id: string;
  type: 'pageview' | 'conversion';
  path: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  sessionId: string;
  country?: string;
  city?: string;
  timestamp: string;
  meta?: Record<string, any>;
}

export interface CMSData {
  siteConfig: {
    name: string;
    nameEnglish: string;
    tagline: string;
    description: string;
    logo: string;
    favicon?: string;
    social: {
      facebook?: string;
      instagram?: string;
      whatsapp?: string;
      wechat?: string;
    };
  };
  nav: { id: string; label: string; href: string; children?: any[] }[];
  hero: {
    backgroundImage: string;
    tagline: string;
    titleLine1: string;
    titleLine2: string;
    description: string;
    primaryButtonText: string;
    primaryButtonLink: string;
    secondaryButtonText: string;
    secondaryButtonLink: string;
  };
  about: {
    enabled: boolean;
    sectionTagline: string;
    sectionTitle: string;
    companyName: string;
    companyNameEnglish: string;
    establishedYear: string;
    description: string;
    businessScope: string[];
    image: string;
  };
  services: {
    enabled: boolean;
    sectionTagline: string;
    sectionTitle: string;
    sectionDescription: string;
    items: { id: string; icon: string; title: string; description: string; }[];
  };
  properties: Property[];
  agents: Agent[];
  news: NewsItem[];
  contact: {
    enabled: boolean;
    sectionTagline: string;
    sectionTitle: string;
    sectionDescription: string;
    info: {
      phone: string;
      phoneDisplay: string;
      whatsapp: string[];
      email: string;
      address: string;
      addressDisplay: string;
      businessHours: string;
      mapLink?: string;
    };
  };
  ownerForm: {
    enabled: boolean;
    title: string;
    description: string;
    fields: {
      id: string;
      label: string;
      type: 'text'|'tel'|'email'|'select'|'textarea'|'number';
      placeholder?: string;
      required: boolean;
      options?: {label:string;value:string}[];
    }[];
    whatsappNotificationNumber?: string;
  };
  footer: {
    companyName: string;
    companyNameEnglish: string;
    description: string;
    copyright: string;
    licenseNumber: string;
    links: {label:string;href:string}[];
  };
  lastUpdated?: string;
}
\`\`\`

---

## 5. Worker 骨架（apps/cms-worker/src/index.ts）

使用 Hono，定義所有路由和基本 handler 結構：

\`\`\`
GET    /                          health check
GET    /api/cms/data              取得 CMS 資料（deep merge with default）
POST   /api/cms/data              更新 CMS 資料（deep merge，需 auth）
POST   /api/cms/reset             重置為 default（需 auth）
POST   /api/admin/login           登入
POST   /api/inquiries             提交查詢
GET    /api/inquiries             列出查詢（需 auth）
PUT    /api/inquiries/:id         更新查詢狀態（需 auth）
POST   /api/upload                上傳媒體（需 auth，檢查 1GB 上限）
GET    /api/media/:id             取得媒體內容
GET    /api/media                 列出媒體 + usage（需 auth）
DELETE /api/media/:id             刪除媒體（需 auth）
POST   /api/analytics/event       寫入 analytics 事件
GET    /api/analytics             analytics 彙總（需 auth）
GET    /api/analytics/campaigns   campaign 清單（需 auth）
\`\`\`

### Deep Merge 規則
- 物件：遞迴合併
- 陣列與基本型別：source 覆蓋 target
- 每次寫入更新 \`lastUpdated\` 為 ISO8601
- 同時寫入 \`cms_history_{ISO8601}\`，設定 \`expirationTtl: 86400 * 30\`

### Auth 驗證
\`\`\`ts
function verifyAdmin(auth: string | undefined, env: Env): boolean {
  const token = auth?.replace("Bearer ", "").trim() ?? "";
  const expectedPassword = env.ADMIN_PASSWORD || "admin360";
  return token === "marveland-admin-token" ||
    (auth?.startsWith("Basic ") && atob(auth.slice(6)) === \`\${env.ADMIN_USERNAME}:\${expectedPassword}\`);
}
\`\`\`
- \`ADMIN_USERNAME\` 在 \`wrangler.toml\` 設為 \`Marveland\`
- 生產 \`ADMIN_PASSWORD\` 必須用 \`wrangler secret put ADMIN_PASSWORD\`，不要寫死

---

## 6. 前台骨架

### 路由結構
\`\`\`
/              → Home.tsx (placeholder: <div>Home</div>)
/about         → About.tsx (placeholder)
/properties    → Properties.tsx (placeholder)
/properties/:id → PropertyDetail.tsx (placeholder)
/owner-listing → OwnerListing.tsx (placeholder)
/contact       → ContactPage.tsx (placeholder)
/admin         → Admin.tsx (placeholder: <div>Admin Login Placeholder</div>)
*              → NotFound.tsx (placeholder)
\`\`\`

### CMSContext.tsx 骨架
- 初始化使用靜態 \`defaultCMSData\`（從 default.ts 同步的副本）
- mount 後呼叫 \`VITE_CMS_API_URL/api/cms/data\` 取得最新資料
- 提供 \`updateCMSData(partial)\`、\`submitInquiry(inquiry)\`、\`getAgent(id)\`、\`getProperty(id)\`
- **當前只需建立 interface 和空實現**

### 必要工具函數骨架
- \`utils/helpers.ts\`：空檔案，只 export 占位符
- \`utils/propertyFilters.ts\`：定義 \`filterProperties(properties, filters)\` 函數簽名，實現為 \`return properties\`

---

## 7. 配置檔案

### apps/web/vite.config.ts
\`\`\`ts
import { defineConfig } from 'vite';
import react from '@vitejs/plugin-react';
export default defineConfig({ plugins: [react()], build: { outDir: 'dist', sourcemap: true } });
\`\`\`

### apps/web/tailwind.config.js
\`\`\`js
export default { content: ['./index.html', './src/**/*.{js,ts,jsx,tsx}'], theme: { extend: {} }, plugins: [] };
\`\`\`

### apps/web/postcss.config.js
\`\`\`js
export default { plugins: { tailwindcss: {}, autoprefixer: {} } };
\`\`\`

### apps/web/tsconfig.app.json
\`\`\`json
{
  "compilerOptions": {
    "target": "ES2020", "useDefineForClassFields": true,
    "lib": ["ES2020", "DOM", "DOM.Iterable"], "module": "ESNext",
    "skipLibCheck": true, "moduleResolution": "bundler",
    "allowImportingTsExtensions": true, "resolveJsonModule": true,
    "isolatedModules": true, "noEmit": true, "jsx": "react-jsx",
    "strict": true, "noUnusedLocals": true, "noUnusedParameters": true,
    "noFallthroughCasesInSwitch": true
  },
  "include": ["src"]
}
\`\`\`

### apps/web/package.json
\`\`\`json
{
  "name": "marveland-web", "private": true, "version": "1.0.0", "type": "module",
  "scripts": {
    "dev": "vite", "build": "tsc && vite build", "preview": "vite preview",
    "deploy": "wrangler pages deploy dist --project-name=marveland"
  },
  "dependencies": {
    "react": "^18.3.1", "react-dom": "^18.3.1", "react-router-dom": "^6.25.1",
    "lucide-react": "^0.400.0", "framer-motion": "^11.3.0", "recharts": "^2.12.7"
  },
  "devDependencies": {
    "@types/react": "^18.3.3", "@types/react-dom": "^18.3.0",
    "@vitejs/plugin-react": "^4.3.1", "autoprefixer": "^10.4.19",
    "postcss": "^8.4.39", "tailwindcss": "^3.4.6",
    "typescript": "^5.5.3", "vite": "^5.3.4", "wrangler": "^3.65.0"
  }
}
\`\`\`

### apps/cms-worker/package.json
\`\`\`json
{
  "name": "marveland-cms-worker", "version": "1.0.0", "type": "module",
  "scripts": {
    "dev": "wrangler dev", "deploy": "wrangler deploy"
  },
  "dependencies": { "hono": "^4.5.0" },
  "devDependencies": { "@cloudflare/workers-types": "^4.20240718.0", "typescript": "^5.5.3", "wrangler": "^3.65.0" }
}
\`\`\`

### apps/cms-worker/wrangler.toml
\`\`\`toml
name = "marveland-cms"
main = "src/index.ts"
compatibility_date = "2025-07-18"

[[kv_namespaces]]
binding = "CMS_DATA"
id = "YOUR_KV_NAMESPACE_ID"

# 可選 R2
# [[r2_buckets]]
# binding = "MEDIA_BUCKET"
# bucket_name = "marveland-media"

[vars]
ADMIN_USERNAME = "Marveland"
\`\`\`

### 根 package.json
\`\`\`json
{
  "name": "marveland", "version": "1.0.0", "private": true,
  "scripts": {
    "dev": "concurrently \\"cd apps/web && npm run dev\\" \\"cd apps/cms-worker && npm run dev\\"",
    "build": "cd apps/web && npm run build",
    "deploy": "cd apps/cms-worker && npm run deploy && cd ../web && npm run deploy"
  },
  "devDependencies": { "concurrently": "^8.2.2" }
}
\`\`\`

---

## 8. default.ts 內容要求

\`apps/cms-worker/src/data/default.ts\` 必須匯出 \`defaultCMSData: CMSData\`，包含：
- 上述所有品牌資料
- 7 位初始銷售團隊成員
- 至少 5 筆示例物業（涵蓋 sale/rent/sale_or_rent、不同 district、不同 category）
- 至少 2 筆最新消息
- 完整的聯絡與頁腳資料
- 完整的 nav、hero、about、services、ownerForm 配置

---

## 9. 生成完成後的驗證步驟

1. 檢查 \`apps/web/src/types/cms.ts\` 與 \`apps/cms-worker/src/types.ts\` 是否逐字一致
2. 檢查 \`wrangler.toml\` 的 \`ADMIN_PASSWORD\` 是否**沒有**出現在 \`[vars]\` 中
3. 檢查 \`apps/web/public/_redirects\` 是否存在且內容為 \`/* /index.html 200\`
4. 檢查 \`apps/web/.env\` 是否存在（至少 \`.env.example\`）
5. 確認 \`npm install\` 在前端和 Worker 都成功
6. 確認 \`npm run build\` 在前端成功輸出 \`dist/\`
7. 確認 TypeScript 無錯誤
8. 確認 Worker 的 \`CMS_DATA\` binding 型別與 \`wrangler.toml\` 一致
9. 確認 \`defaultCMSData\` 滿足 \`CMSData\` interface 的所有必填欄位
10. 確認所有 placeholder 頁面都有基本 export（避免 \`noUnusedLocals\` 報錯）

---

## 10. 常見陷阱（Layer 1 特別關注）

1. **型別同步**：兩個 types 檔案必須逐字一致
2. **Deep Merge**：Worker 的 POST /api/cms/data 必須 deep merge，不可用淺拷貝
3. **CORS**：Worker 必須回應 CORS headers，且 cors middleware 要在所有路由之前
4. **Admin 密碼**：不可寫死在任何檔案中（除了本地 .dev.vars）
5. **KV binding 名稱**：\`wrangler.toml\` 的 \`binding\` 必須與 \`Env\` interface 的屬性名稱一致
6. **placeholder 報錯**：\`noUnusedLocals\` 開啟時，placeholder import 會報錯，確保每個 import 都有使用
7. **Framer Motion / Recharts**：雖然 package.json 包含，但骨架階段不 import，避免報錯
`;
